/**
 * B1120 — Révision guidée des synthèses périmées (Eric 2026-09-08).
 *
 * POURQUOI
 * ────────
 * Le tableau listait les synthèses périmées, mais traiter la liste imposait :
 * ouvrir la vidéo → juger → revenir au tableau → retrouver sa place. Personne
 * ne vide une liste de 20 lignes comme ça. Ici on enchaîne les fiches une par
 * une avec « Suivant », dans l'ordre des modifications les plus récentes.
 *
 * Chaque fiche montre le texte ET ce qui a changé depuis la vérification :
 * sans le diff, impossible de savoir si la modification mérite un appel
 * facturé. D'où la troisième issue, « vérifiée sans appel IA » : une
 * reformulation cosmétique ne doit pas coûter une vérification.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, ArrowSquareOut, CheckCircle, ListChecks, SkipForward, Sparkle, X,
} from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(iso);
  }
};

export const GuidedStaleReview = ({ rows = [], onDone }) => {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [tally, setTally] = useState({ reverified: 0, manual: 0, skipped: 0 });

  // Ordre : les plus récemment modifiées d'abord (choix d'Eric). C'est
  // l'ordre du serveur, indépendant du tri d'affichage du tableau.
  const queue = [...rows].sort((a, b) =>
    String(b.changed_at || "").localeCompare(String(a.changed_at || "")));
  const total = queue.length;
  const current = queue[idx] || null;
  const finished = open && idx >= total;

  const loadCard = useCallback(async (synthesisId) => {
    setLoading(true);
    setCard(null);
    try {
      const { data } = await axios.get(
        `${API}/api/admin/stale-syntheses/${synthesisId}/card`,
        { withCredentials: true }
      );
      setCard(data);
    } catch (e) {
      toast.error("Fiche indisponible : " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && current) loadCard(current.synthesis_id);
  }, [open, current, loadCard]);

  const start = () => {
    setIdx(0);
    setTally({ reverified: 0, manual: 0, skipped: 0 });
    setOpen(true);
  };

  const close = async () => {
    setOpen(false);
    setCard(null);
    if (onDone) await onDone();
  };

  const next = () => setIdx((i) => i + 1);

  const skip = () => {
    setTally((t) => ({ ...t, skipped: t.skipped + 1 }));
    next();
  };

  const reverify = async () => {
    if (!current) return;
    setBusy("reverify");
    try {
      await axios.post(
        `${API}/api/admin/synthesis-verifications/reverify`,
        { synthesis_ids: [current.synthesis_id] },
        { withCredentials: true }
      );
      // La vérification tourne en tâche de fond : on n'immobilise pas la
      // révision guidée pendant 30 s d'appel fournisseur.
      toast.info("Vérification lancée en arrière-plan.");
      setTally((t) => ({ ...t, reverified: t.reverified + 1 }));
      next();
    } catch (e) {
      toast.error("Lancement impossible : " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy("");
    }
  };

  const markManual = async () => {
    if (!current) return;
    setBusy("manual");
    try {
      await axios.post(
        `${API}/api/admin/stale-syntheses/${current.synthesis_id}/mark-verified`,
        { note: "Révision guidée : modification sans impact factuel" },
        { withCredentials: true }
      );
      toast.success("Marquée « revue manuellement », sans appel IA.");
      setTally((t) => ({ ...t, manual: t.manual + 1 }));
      next();
    } catch (e) {
      toast.error("Enregistrement impossible : " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy("");
    }
  };

  return (
    <>
      <button
        onClick={start}
        disabled={total === 0}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#002FA7] text-[#002FA7] text-sm font-medium hover:bg-[#002FA7]/5 disabled:opacity-40"
        data-testid="guided-review-btn"
        title={total === 0
          ? "Aucune synthèse périmée à réviser"
          : `Enchaîner les ${total} synthèses périmées une par une`}
      >
        <ListChecks size={16} />
        Révision guidée ({total})
      </button>

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="guided-review-dialog"
        >
          {finished ? (
            <div className="text-center py-6" data-testid="guided-review-done">
              <CheckCircle size={44} className="text-emerald-600 mx-auto" />
              <h3 className="text-base md:text-lg font-semibold text-zinc-900 mt-3">
                Révision terminée
              </h3>
              <p className="text-sm text-zinc-600 mt-2" data-testid="guided-review-tally">
                {tally.reverified + tally.manual} traitée(s) · {tally.skipped} passée(s)
                {tally.reverified > 0 && ` · ${tally.reverified} re-vérification(s) en arrière-plan`}
              </p>
              <button
                onClick={close}
                className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#002FA7] text-white text-sm font-medium hover:bg-[#00246f]"
                data-testid="guided-review-close"
              >
                <X size={15} />
                Fermer
              </button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-base md:text-lg">
                  <ListChecks size={20} className="text-[#002FA7]" />
                  Révision guidée — {idx + 1} / {total}
                </DialogTitle>
                <DialogDescription asChild>
                  <div className="text-sm text-zinc-600">
                    <span className="font-medium text-zinc-900" data-testid="guided-review-filename">
                      {current?.filename || current?.title || "—"}
                    </span>
                    <span className="block text-xs text-zinc-500" data-testid="guided-review-title">
                      {current?.title}
                    </span>
                    <span className="block text-xs mt-1">
                      Vérifiée le {fmt(card?.verified_at || current?.verified_at)} ·{" "}
                      <span className="text-amber-700">
                        modifiée le {fmt(card?.changed_at || current?.changed_at)}
                        {card?.changed_by ? ` par ${card.changed_by}` : ""}
                      </span>
                    </span>
                  </div>
                </DialogDescription>
              </DialogHeader>

              {loading ? (
                <p className="text-sm text-zinc-500 py-8 text-center" data-testid="guided-review-loading">
                  Chargement de la fiche…
                </p>
              ) : (
                <div className="space-y-4">
                  {/* Ce qui a changé : sans ça, impossible de juger si la
                      modification mérite un appel facturé. */}
                  <section data-testid="guided-review-diff">
                    <h4 className="text-xs uppercase tracking-wide text-zinc-500 mb-1.5">
                      Ce qui a changé depuis la vérification
                    </h4>
                    {!card?.diff_available ? (
                      <p className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2"
                         data-testid="guided-review-diff-unavailable">
                        Version d'avant non conservée pour cette synthèse : le texte complet
                        est affiché ci-dessous.
                      </p>
                    ) : card.diff.length === 0 ? (
                      <p className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2"
                         data-testid="guided-review-diff-empty">
                        Aucune différence de texte détectée (titre ou métadonnée modifiée).
                      </p>
                    ) : (
                      <div className="max-h-48 overflow-y-auto rounded-lg border border-zinc-200 divide-y divide-zinc-100">
                        {card.diff.map((d, i) => (
                          <p
                            key={i}
                            className={`px-3 py-1.5 text-xs font-mono whitespace-pre-wrap ${
                              d.type === "add"
                                ? "bg-emerald-50 text-emerald-900"
                                : "bg-red-50 text-red-900 line-through decoration-red-300"
                            }`}
                            data-testid={`guided-review-diff-${d.type}-${i}`}
                          >
                            {d.type === "add" ? "+ " : "− "}{d.text}
                          </p>
                        ))}
                        {card.diff_truncated && (
                          <p className="px-3 py-1.5 text-[11px] text-zinc-500">
                            Diff tronqué — ouvrir la vidéo pour le détail complet.
                          </p>
                        )}
                      </div>
                    )}
                  </section>

                  <section>
                    <h4 className="text-xs uppercase tracking-wide text-zinc-500 mb-1.5">
                      Synthèse actuelle ({card?.word_count || 0} mots)
                    </h4>
                    <div
                      className="max-h-64 overflow-y-auto rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-800 whitespace-pre-wrap"
                      data-testid="guided-review-body"
                    >
                      {card?.body || "Aucun texte."}
                    </div>
                  </section>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-zinc-200">
                <button
                  onClick={reverify}
                  disabled={!!busy}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#002FA7] text-white text-sm font-medium hover:bg-[#00246f] disabled:opacity-50"
                  data-testid="guided-review-reverify"
                  title="Relance la vérification factuelle : 1 appel facturé au fournisseur"
                >
                  <Sparkle size={15} />
                  {busy === "reverify" ? "Lancement…" : "Re-vérifier (1 appel facturé)"}
                </button>
                {/* Sortie honnête pour les modifs cosmétiques : la date de
                    vérification est rafraîchie, mais la trace dit « manuelle ». */}
                <button
                  onClick={markManual}
                  disabled={!!busy}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-600 text-emerald-800 text-sm font-medium hover:bg-emerald-50 disabled:opacity-50"
                  data-testid="guided-review-mark-verified"
                  title="Sort la synthèse de la liste sans appel IA — la trace indique « revue manuelle »"
                >
                  <CheckCircle size={15} />
                  {busy === "manual" ? "Enregistrement…" : "Vérifiée sans appel IA"}
                </button>
                <button
                  onClick={skip}
                  disabled={!!busy}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-300 text-zinc-700 text-sm hover:bg-zinc-50 disabled:opacity-50"
                  data-testid="guided-review-skip"
                  title="Laisse la synthèse dans la liste et passe à la suivante"
                >
                  <SkipForward size={15} />
                  Passer
                </button>
                <a
                  href={`/dashboard/videos/${current?.video_id || ""}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-300 text-zinc-700 text-sm hover:bg-zinc-50"
                  data-testid="guided-review-open-video"
                  title="Ouvre la vidéo dans un nouvel onglet : la révision guidée reste ouverte ici"
                >
                  <ArrowSquareOut size={15} />
                  Ouvrir la vidéo
                </a>
                <span className="hidden sm:block flex-1" />
                {/* « Suivant » laisse la synthèse dans la liste, exactement
                    comme « Passer » : elle est donc comptée comme passée, sinon
                    l'écran final annoncerait des lignes traitées qui ne le
                    sont pas. */}
                <button
                  onClick={skip}
                  disabled={!!busy}
                  className="ml-auto inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-300 text-zinc-800 text-sm font-semibold hover:bg-zinc-50 disabled:opacity-50"
                  data-testid="guided-review-next"
                >
                  Suivant
                  <ArrowRight size={15} />
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GuidedStaleReview;
